// Profile.js - Account Management Hub
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../Services/Api';
import styles from './Profile.module.css';

function Profile({ userData, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [friendsCount, setFriendsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Form states
  const [editForm, setEditForm] = useState({ username: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({ 
    currentPassword: '', 
    newPassword: '', 
    confirmPassword: '' 
  });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  useEffect(() => {
    fetchProfileData();
  }, []);

  useEffect(() => {
    if (userData) {
      setEditForm({ 
        username: userData.username || '', 
        email: userData.email || '' 
      });
    }
  }, [userData]);

  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const [statsRes, friendsRes] = await Promise.all([
        api.get('stats/streak/'),
        api.get('friends/')
      ]);
      setStats(statsRes.data);
      setFriendsCount(friendsRes.data.length);
    } catch (err) {
      console.error('Failed to fetch profile data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysSinceJoining = () => {
    if (!userData?.date_joined) return 0;
    const joined = new Date(userData.date_joined);
    const today = new Date();
    return Math.ceil(Math.abs(today - joined) / (1000 * 60 * 60 * 24));
  };

  const handleLogout = () => {
    localStorage.clear();
    if (onLogout) onLogout();
    navigate('/login');
  };

  // Handle Edit Profile
  const handleEditProfile = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    
    if (!editForm.username.trim()) {
      setFormError('Username is required');
      return;
    }
    
    try {
      // Note: You'll need to create this endpoint in backend
      await api.put('profile/', editForm);
      setFormSuccess('Profile updated successfully!');
      setTimeout(() => {
        setShowEditModal(false);
        setFormSuccess('');
        window.location.reload(); // Refresh to get updated data
      }, 1500);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to update profile');
    }
  };

  // Handle Change Password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setFormError('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    
    try {
      // Note: You'll need to create this endpoint in backend
      await api.post('profile/change-password/', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      setFormSuccess('Password changed successfully!');
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setFormSuccess('');
      }, 1500);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to change password');
    }
  };

  // Handle Delete Account
  const handleDeleteAccount = async () => {
    try {
      // Note: You'll need to create this endpoint in backend
      await api.delete('profile/');
      localStorage.clear();
      if (onLogout) onLogout();
      navigate('/login');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to delete account');
    }
  };

  if (loading) {
    return (
      <div className={styles.profileContainer}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileContainer}>
      <div className={styles.profileHeader}>
        <h1 className={styles.title}>Account Settings</h1>
        <p className={styles.subtitle}>Manage your profile and preferences</p>
      </div>

      <div className={styles.profileContent}>
        {/* User Info Card */}
        <div className={styles.userCard}>
          <div className={styles.avatarSection}>
            <div className={styles.avatar}>
              {userData?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <h2 className={styles.username}>{userData?.username || 'User'}</h2>
            <p className={styles.userEmail}>{userData?.email || 'No email'}</p>
          </div>

          <div className={styles.statsRow}>
            <div className={styles.statBox}>
              <span className={styles.statIcon}>🔥</span>
              <span className={styles.statNumber}>{stats?.current_streak || 0}</span>
              <span className={styles.statLabel}>Current Streak</span>
            </div>
            <div className={styles.statBox}>
              <span className={styles.statIcon}>🏆</span>
              <span className={styles.statNumber}>{stats?.longest_streak || 0}</span>
              <span className={styles.statLabel}>Best Streak</span>
            </div>
            <div className={styles.statBox}>
              <span className={styles.statIcon}>👥</span>
              <span className={styles.statNumber}>{friendsCount}</span>
              <span className={styles.statLabel}>Friends</span>
            </div>
          </div>

          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Member Since</span>
              <span className={styles.infoValue}>{formatDate(userData?.date_joined)}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Days on Journey</span>
              <span className={styles.infoValue}>{getDaysSinceJoining()} days</span>
            </div>
          </div>
        </div>

        {/* Account Actions Card */}
        <div className={styles.actionsCard}>
          <h3 className={styles.sectionTitle}>Account Management</h3>
          
          <div className={styles.actionsList}>
            <button 
              className={styles.actionItem}
              onClick={() => setShowEditModal(true)}
            >
              <span className={styles.actionIcon}>✏️</span>
              <div className={styles.actionInfo}>
                <span className={styles.actionName}>Edit Profile</span>
                <span className={styles.actionDesc}>Update your username and email</span>
              </div>
              <span className={styles.actionArrow}>›</span>
            </button>

            <button 
              className={styles.actionItem}
              onClick={() => setShowPasswordModal(true)}
            >
              <span className={styles.actionIcon}>🔒</span>
              <div className={styles.actionInfo}>
                <span className={styles.actionName}>Change Password</span>
                <span className={styles.actionDesc}>Update your login password</span>
              </div>
              <span className={styles.actionArrow}>›</span>
            </button>
          </div>

          <div className={styles.sessionSection}>
            <h4 className={styles.sessionTitle}>Session</h4>
            <button className={styles.logoutButton} onClick={handleLogout}>
              🚪 Log Out
            </button>
          </div>

          <div className={styles.dangerSection}>
            <h4 className={styles.dangerTitle}>Danger Zone</h4>
            <button 
              className={styles.deleteButton}
              onClick={() => setShowDeleteModal(true)}
            >
              🗑️ Delete Account
            </button>
            <p className={styles.dangerNote}>
              Permanently delete your account and all data
            </p>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>✏️ Edit Profile</h3>
            
            {formError && <div className={styles.formError}>{formError}</div>}
            {formSuccess && <div className={styles.formSuccess}>{formSuccess}</div>}
            
            <form onSubmit={handleEditProfile}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Username</label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                  className={styles.formInput}
                  placeholder="Enter username"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className={styles.formInput}
                  placeholder="Enter email"
                />
              </div>
              
              <div className={styles.modalActions}>
                <button 
                  type="button"
                  className={styles.modalCancel}
                  onClick={() => {
                    setShowEditModal(false);
                    setFormError('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.modalSubmit}>
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className={styles.modalOverlay} onClick={() => setShowPasswordModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>🔒 Change Password</h3>
            
            {formError && <div className={styles.formError}>{formError}</div>}
            {formSuccess && <div className={styles.formSuccess}>{formSuccess}</div>}
            
            <form onSubmit={handleChangePassword}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Current Password</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                  className={styles.formInput}
                  placeholder="Enter current password"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  className={styles.formInput}
                  placeholder="Enter new password"
                />
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  className={styles.formInput}
                  placeholder="Confirm new password"
                />
              </div>
              
              <div className={styles.modalActions}>
                <button 
                  type="button"
                  className={styles.modalCancel}
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setFormError('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.modalSubmit}>
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>⚠️ Delete Account</h3>
            
            {formError && <div className={styles.formError}>{formError}</div>}
            
            <p className={styles.modalText}>
              This action <strong>cannot be undone</strong>. All your data will be permanently deleted:
            </p>
            <ul className={styles.deleteList}>
              <li>Your streak history</li>
              <li>All your goals</li>
              <li>Friend connections</li>
              <li>Daily logs and insights</li>
            </ul>
            
            <p className={styles.modalText}>
              Type <strong>DELETE</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className={styles.formInput}
              placeholder="Type DELETE"
            />
            
            <div className={styles.modalActions}>
              <button 
                className={styles.modalCancel}
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                  setFormError('');
                }}
              >
                Cancel
              </button>
              <button 
                className={styles.modalDelete}
                disabled={deleteConfirmText !== 'DELETE'}
                onClick={handleDeleteAccount}
              >
                Delete My Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;